import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

const timelineEvents = [
  { year: '2024.06', title: '平台立项', desc: '创始团队洞察到中文互联网福利信息碎片化痛点，决定打造一站式福利聚合平台。' },
  { year: '2024.09', title: '内测上线', desc: '福利导航Beta版上线，首批覆盖优惠券、会员权益、生活补贴三大核心分类。' },
  { year: '2025.01', title: '正式发布', desc: '福利导航1.0版本正式发布，分类扩展至6大模块，月活用户突破10万。' },
  { year: '2025.06', title: '内容升级', desc: '引入专业编辑团队，所有福利信息实现人工核实，确保100%真实可靠。' },
  { year: '2025.12', title: '用户突破', desc: '月活用户突破50万，成为中文福利信息领域最具影响力的聚合平台之一。' },
  { year: '2026.03', title: '全面升级', desc: '覆盖8大福利分类，新增金融福利与旅游福利模块，每周更新200+篇福利内容。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '白领 / 上海',
    content: '福利导航帮我省了不少钱！上个月通过推荐的信用卡权益，吃饭加油省了500多。特别是每周的信用卡周报，简直是薅羊毛神器。',
    rating: 5,
  },
  {
    name: '李同学',
    role: '大学生 / 北京',
    content: '作为大学生，通过福利导航了解到很多之前不知道的奖学金和助学政策。平台推荐的免费学习资源也很实用，强烈推荐给学弟学妹。',
    rating: 5,
  },
  {
    name: '王女士',
    role: '宝妈 / 广州',
    content: '育儿补贴那篇文章帮了大忙！之前不知道二孩每月能领800元补贴，申请下来一年多了近万元。感谢福利导航的及时信息。',
    rating: 5,
  },
  {
    name: '赵经理',
    role: 'HR / 深圳',
    content: '我把福利导航推荐给了全公司的同事，员工福利板块的内容特别全面。培训补贴、考证报销这些隐性福利很多人不知道，这个平台做了很好的科普。',
    rating: 5,
  },
  {
    name: '陈先生',
    role: '个体户 / 成都',
    content: '电商优惠券汇总特别实用，每次大促前来福利导航逛一圈，该领的券一个不落。一年下来比之前至少省了2000元。',
    rating: 5,
  },
  {
    name: '刘女士',
    role: '教师 / 杭州',
    content: '旅游福利板块是我最常看的，暑假带家人出游靠景区的教师优惠省了不少门票费。信息更新很及时，出发前查一查总能发现惊喜。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const handleContact = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, message } = contactForm;
    if (!name || !email || !message) {
      setContactError('请填写所有必填字段');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setContactError('请输入有效的邮箱地址');
      return;
    }
    if (message.length > 500) {
      setContactError('留言内容不能超过500个字符');
      return;
    }
    setContactStatus('sending');
    setContactError('');

    try {
      const form = e.currentTarget as HTMLFormElement;
      const formData = new FormData(form);

      const honeypot = (formData.get('company_alt') as string || '').trim();
      if (honeypot) {
        setContactStatus('success');
        setContactForm({ name: '', email: '', message: '' });
        return;
      }

      const res = await fetch('https://readdy.ai/api/form/d9qmp6ue3oq4jqumtn60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(
            Array.from(formData.entries()).filter(([_, v]) => typeof v === 'string')
          ) as Record<string, string>
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setContactStatus('error');
        setContactError(parsed?.meta?.message || '发送失败，请稍后重试');
      }
    } catch {
      setContactStatus('error');
      setContactError('网络错误，请稍后重试');
    }
  }, [contactForm]);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于福利导航',
    description: '福利导航是一站式福利资源聚合平台，精准聚合各类福利资源，涵盖优惠券、会员权益、生活补贴、职场福利等多维度内容。',
    url: 'https://fulidaohang.com/about',
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '福利导航',
      url: 'https://fulidaohang.com',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@fulidaohang.com',
      },
    },
  };

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulidaohang.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <Navbar />
      <title>关于我们 - 品牌介绍 | 福利导航</title>
      <meta name="description" content="福利导航是一站式福利资源聚合平台，精准聚合各类福利资源。了解我们的品牌故事、团队理念、用户口碑和联系方式。" />
      <meta name="keywords" content="关于福利导航,福利导航品牌,福利平台介绍,联系我们" />
      <link rel="canonical" href="https://fulidaohang.com/about" />
      <meta property="og:title" content="关于我们 - 品牌介绍 | 福利导航" />
      <meta property="og:description" content="福利导航是一站式福利资源聚合平台，精准聚合各类福利资源。了解我们的品牌故事、团队理念、用户口碑和联系方式。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://fulidaohang.com/about" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="关于我们 - 品牌介绍 | 福利导航" />
      <meta name="twitter:description" content="福利导航是一站式福利资源聚合平台，精准聚合各类福利资源。" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }} />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-3">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              关于我们
            </h1>
            <p className="text-foreground-600 max-w-2xl">
              了解福利导航的品牌故事与发展历程
            </p>
          </div>
        </div>

        {/* Brand Intro */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                品牌简介
              </h2>
              <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                福利导航创立于2024年，致力于成为中文互联网最值得信赖的福利资源聚合平台。
                我们精准聚合各类福利资源，涵盖优惠券、会员权益、生活补贴、职场福利、教育福利、
                健康福利、旅游福利、金融福利等8大维度内容，帮助用户一站式发现最新最全福利信息，
                节省时间与金钱，让每一分钱都花得值。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-eye-line', title: '使命', desc: '让每个人都能轻松发现和享受属于自己的福利权益，不错过任何应得的实惠。' },
                { icon: 'ri-flag-line', title: '愿景', desc: '成为中文互联网福利信息的第一入口，构建最全面、最及时、最可信的福利生态。' },
                { icon: 'ri-heart-line', title: '价值观', desc: '真实、及时、全面、免费。所有福利信息人工核实，绝不发布虚假或过时内容。' },
              ].map((item) => (
                <div key={item.title} className="bg-background-100 border border-background-200/70 rounded-xl p-6 text-center">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-foreground-600">从想法到现实，福利导航的成长之路</p>
            </div>
            <div className="relative">
              <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>
              <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-background-300 md:hidden"></div>
              <div className="flex flex-col gap-8">
                {timelineEvents.map((event, idx) => (
                  <div key={event.year} className={`relative flex gap-6 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 md:px-8 flex md:justify-end">
                      {idx % 2 === 0 && (
                        <div className="hidden md:block bg-background-50 border border-background-200/70 rounded-xl p-5 max-w-sm">
                          <p className="text-xs font-bold text-primary-500 mb-1">{event.year}</p>
                          <h4 className="font-heading text-base font-semibold text-foreground-950 mb-1">{event.title}</h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{event.desc}</p>
                        </div>
                      )}
                    </div>
                    <div className="absolute left-[15px] md:left-1/2 md:-translate-x-1/2 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-background-50 mt-1.5"></div>
                    <div className="md:w-1/2 md:px-8 pl-10 md:pl-8">
                      <div className={`bg-background-50 border border-background-200/70 rounded-xl p-5 ${idx % 2 === 0 ? 'md:hidden' : ''}`}>
                        <p className="text-xs font-bold text-accent-500 mb-1">{event.year}</p>
                        <h4 className="font-heading text-base font-semibold text-foreground-950 mb-1">{event.title}</h4>
                        <p className="text-sm text-foreground-600 leading-relaxed">{event.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600">50万+用户的选择与信赖</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <span className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-700 text-sm"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600">
                有任何问题或建议？欢迎通过以下方式联系
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'contact@fulidaohang.com' },
                { icon: 'ri-wechat-line', label: '微信公众号', value: '福利导航' },
                { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00' },
              ].map((item) => (
                <div key={item.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
                  <span className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-2">
                    <i className={`${item.icon} text-accent-600`}></i>
                  </span>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
                在线留言
              </h3>
              {contactStatus === 'success' ? (
                <div className="text-center py-8">
                  <span className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </span>
                  <p className="text-foreground-900 font-medium">留言已发送，感谢您的反馈！</p>
                  <p className="text-sm text-foreground-500 mt-1">我们将在1-2个工作日内回复</p>
                </div>
              ) : (
                <form onSubmit={handleContact} data-readdy-form="true">
                  <div className="hp-field">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入姓名"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入邮箱"
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                      <span className="text-foreground-400 font-normal ml-1">（最多500字）</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={contactForm.message}
                      onChange={(e) => setContactForm((p) => ({ ...p, message: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请描述您的问题或建议..."
                      required
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">
                      {contactForm.message.length}/500
                    </p>
                  </div>
                  {contactError && (
                    <p className="text-sm text-primary-500 mb-3">{contactError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={contactStatus === 'sending'}
                    className="w-full py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm"
                  >
                    {contactStatus === 'sending' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}